/**
 * LA FICHA DE UN AGENTE, SIN DOM (FICHA-PERSONA-AGENTE-T1, Ares #33978; mapa de Atenea #33972, d6ad10e8).
 *
 * Junta en un solo lugar lo que el panel YA lee de un agente, cada cosa con su fuente y su enlace:
 *   - LO QUE SABE (demostrado): su pensum propio (unidades aprobadas, learning/pensum/agentes) y el pensum comun de Nexo
 *     (unidades verificadas, learning/pensum/nexo). Aprobada/verificada es lo que cuenta; citada no es dominio.
 *   - LO QUE PUEDE (autorizado): instrucciones que le aplican y sus llaves activas con sus permisos, de GET admin/agents/{code}/puede
 *     (nexo-api PR 631). Mientras esa ruta de 404 se dice "sin fuente todavia", no se inventa ni se pinta vacio como cero.
 *   - Lo demas que ya existe: insignias (/agent-badges?agentCode=) y NXS (/nxs/transactions?user_id=).
 * Reglas del mapa: separado lo que sabe de lo que puede; sin semaforo verde sin evaluacion; un error de lectura no es 0.
 *
 * No consulta nada: recibe lo que la vista ya pidio. Lo prueba scripts/check-ficha-agente.mjs.
 *
 * @module application/agentes/fichaDeAgente
 */

const texto = (v) => (v === undefined || v === null ? '' : String(v).trim());
const mismo = (a, b) => texto(a).toUpperCase() === texto(b).toUpperCase();

/** Una lectura que fallo (null/undefined) NO es una lista vacia: se dice que no se pudo leer. */
export const NO_LEIDO = 'no_leido';

/**
 * @param {string} codigo agent_code
 * @param {{pensumAgentes?: Array|null, pensumNexo?: {agentes: Array}|null, insignias?: Array|null, transacciones?: Array|null}} fuentes
 *   lo ya normalizado (normalizarPensumAgentes, normalizarPensumNexo); null = esa lectura fallo
 */
/** La ruta de lo que puede aun no esta publicada (404): no es un error de lectura, es que no hay fuente todavia. */
export const SIN_FUENTE = 'sin_fuente';

/** Lo que puede, desde GET admin/agents/{code}/puede (camelCase; se toleran las dos formas). */
function seccionesPuede(puede) {
  if (puede === undefined || puede === SIN_FUENTE) {
    return [
      { clave: 'instrucciones', titulo: 'Instrucciones que le aplican', fuente: '#/instructions', estado: SIN_FUENTE },
      { clave: 'permisos', titulo: 'Llaves y permisos', fuente: null, estado: SIN_FUENTE }
    ];
  }
  if (puede === null) {
    return [
      { clave: 'instrucciones', titulo: 'Instrucciones que le aplican', fuente: '#/instructions', estado: NO_LEIDO },
      { clave: 'permisos', titulo: 'Llaves y permisos', fuente: null, estado: NO_LEIDO }
    ];
  }
  const instrucciones = puede.instrucciones ?? puede.Instrucciones ?? [];
  const llaves = puede.llaves ?? puede.Llaves ?? [];
  const scopes = [...new Set(llaves.flatMap((l) => l.scopes ?? l.Scopes ?? []))].sort();
  return [
    { clave: 'instrucciones', titulo: 'Instrucciones que le aplican', fuente: '#/instructions', estado: 'con_datos',
      cantidad: instrucciones.length, codigos: instrucciones.map((i) => texto(i.codigo ?? i.Codigo)).filter(Boolean) },
    { clave: 'permisos', titulo: 'Llaves y permisos', fuente: null, estado: 'con_datos',
      llaves: Number(puede.llavesActivas ?? puede.LlavesActivas ?? llaves.length) || 0, scopes }
  ];
}

export function fichaDeAgente(codigo, { pensumAgentes, pensumNexo, insignias, transacciones, puede } = {}) {
  const propio = pensumAgentes == null ? NO_LEIDO : (pensumAgentes.find((a) => mismo(a.agente, codigo)) || null);
  const comun = pensumNexo == null ? NO_LEIDO : ((pensumNexo.agentes || []).find((a) => mismo(a.agente, codigo)) || null);
  const susInsignias = insignias == null ? NO_LEIDO : insignias.filter((b) => mismo(b.agent_code ?? b.agentCode, codigo));
  const susNxs = transacciones == null ? NO_LEIDO : transacciones.filter((t) => mismo(t.user_id ?? t.userId, codigo));
  const saldo = susNxs === NO_LEIDO ? null
    : susNxs.reduce((s, t) => s + (String(t.type) === 'debit' ? -1 : 1) * (Number(t.amount) || 0), 0);

  return {
    codigo: texto(codigo),
    sabe: [
      {
        clave: 'pensum_propio',
        titulo: 'Su pensum',
        fuente: '#/pensum',
        estado: propio === NO_LEIDO ? NO_LEIDO : propio ? 'con_datos' : 'sin_pensum',
        // n de N aprobadas: lo que el servidor da por aprobado; no se calcula porcentaje.
        aprobadas: propio && propio !== NO_LEIDO ? propio.aprobadas : null,
        total: propio && propio !== NO_LEIDO ? propio.total : null,
        siguiente: propio && propio !== NO_LEIDO ? propio.siguiente : ''
      },
      {
        clave: 'pensum_comun',
        titulo: 'Pensum comun de Nexo',
        fuente: '#/pensum',
        estado: comun === NO_LEIDO ? NO_LEIDO : comun ? 'con_datos' : 'sin_pensum',
        // Verificada = demostrado; citada solo dice que lo leyo (no suma a lo que sabe).
        verificadas: comun && comun !== NO_LEIDO ? comun.verificadas : null,
        citadas: comun && comun !== NO_LEIDO ? comun.citadas : null,
        total: comun && comun !== NO_LEIDO ? comun.total : null
      }
    ],
    puede: seccionesPuede(puede),
    tambien: [
      { clave: 'insignias', titulo: 'Insignias', fuente: '#/skills', estado: susInsignias === NO_LEIDO ? NO_LEIDO : 'con_datos', cantidad: susInsignias === NO_LEIDO ? null : susInsignias.length },
      { clave: 'nxs', titulo: 'NXS', fuente: '#/banco', estado: susNxs === NO_LEIDO ? NO_LEIDO : 'con_datos', saldo, movimientos: susNxs === NO_LEIDO ? null : susNxs.length }
    ]
  };
}

/** La frase de una seccion, segun su estado. Nunca "0" para lo que no se pudo leer o no tiene fuente. */
export function fraseDeSeccion(s) {
  if (s.estado === NO_LEIDO) return 'No se pudo leer ahora.';
  if (s.estado === 'sin_fuente') return 'Sin fuente todavia: la consulta de lo que puede un agente aun no esta publicada.';
  if (s.estado === 'sin_pensum') return 'No tiene este pensum.';
  if (s.clave === 'pensum_propio') return `${s.aprobadas} de ${s.total} unidades aprobadas${s.siguiente ? ` · sigue: ${s.siguiente}` : ''}`;
  // normalizarPensumNexo cuenta en "citadas" tambien las verificadas: las que solo estan citadas son la resta.
  if (s.clave === 'pensum_comun') {
    const soloCitadas = Math.max(0, (s.citadas || 0) - (s.verificadas || 0));
    return `${s.verificadas} de ${s.total} unidades verificadas` + (soloCitadas ? ` · ${soloCitadas} solo citada${soloCitadas === 1 ? '' : 's'} (leida, no demostrada)` : '');
  }
  if (s.clave === 'instrucciones') return `${s.cantidad} instruccion${s.cantidad === 1 ? '' : 'es'} vigente${s.cantidad === 1 ? '' : 's'}${s.codigos.length ? ': ' + s.codigos.join(', ') : ''}`;
  if (s.clave === 'permisos') {
    if (!s.llaves) return 'Sin llaves activas: hoy no puede entrar por el MCP.';
    return `${s.llaves} llave${s.llaves === 1 ? '' : 's'} activa${s.llaves === 1 ? '' : 's'}${s.scopes.length ? ' · permisos: ' + s.scopes.join(', ') : ''}`;
  }
  if (s.clave === 'insignias') return `${s.cantidad} insignia${s.cantidad === 1 ? '' : 's'}`;
  if (s.clave === 'nxs') return `Saldo ${s.saldo} NXS en ${s.movimientos} movimiento${s.movimientos === 1 ? '' : 's'}`;
  return '';
}
